import * as XLSX from "xlsx";
import { getRawMaterialPurchasesExport } from "../../services/RawMaterials/RawMaterialService";
import {
  RawMaterialPurchaseListDTO,
  RawMaterialPurchaseFilterExportDTO,
} from "../../models/RawMaterials/RawMaterial-model";

const parseNumber = (val: any) => {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const n = parseFloat(val.replace(/[^0-9.\-]/g, ""));
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

const rupee = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const approxEqual = (a: number, b: number, eps = 1e-6) =>
  Math.abs(a - b) < eps;

// ✅ Compute QC status NAME (no dependency on ID)
const getQcStatusNameByComputation = (item: RawMaterialPurchaseListDTO) => {
  const invoiced = parseNumber(item.invoicedQuantity ?? item.quantityWithUnit);
  const accepted = parseNumber(item.acceptedQuantity ?? item.acceptedQuantityWithUnit);
  const rejected = parseNumber(item.rejectedQuantity ?? item.rejectedQuantityWithUnit);
  const remainingFromApi = parseNumber(item.remainingQuantity ?? item.remainingQuantityWithUnit);

  const sumAR = accepted + rejected;
  const computedRemaining = Math.max(0, invoiced - sumAR);
  const remaining = remainingFromApi > 0 ? remainingFromApi : computedRemaining;

  let statusName = "QC Pending";

  if (approxEqual(invoiced, accepted) && invoiced > 0) {
    statusName = "Accepted";
  } else if (approxEqual(invoiced, rejected) && invoiced > 0) {
    statusName = "Rejected";
  } else if (approxEqual(invoiced, sumAR) && invoiced > 0) {
    statusName = "Accepted & Rejected";
  } else if (approxEqual(invoiced, remaining) && invoiced > 0) {
    statusName = "QC Pending";
  } else if (approxEqual(accepted, 0) && rejected > 0 && !approxEqual(rejected, invoiced)) {
    statusName = "Partially Rejected";
  } else {
    statusName = "Partially Accepted";
  }

  return statusName;
};

// ✅ Get the actual display name from API statuses (by NAME, not ID)
const mapQcStatusDisplayName = (computedName: string, qcStatuses: any[]) => {
  const found = qcStatuses?.find(
    (s: any) => s.name?.toLowerCase() === computedName.toLowerCase()
  );
  return found ? found.name : computedName;
};

// ✅ Prepare export data
const rawMaterialWiseData = (
  rawMaterialList: RawMaterialPurchaseListDTO[],
  qcStatuses: any[]
) => {
  return rawMaterialList.map((item) => {
    const computedQcName = getQcStatusNameByComputation(item);
    const qcStatus = mapQcStatusDisplayName(computedQcName, qcStatuses);

    return {
      "Purchase Date": item.purchaseDate
        ? new Date(item.purchaseDate).toLocaleDateString("en-GB")
        : "",
      "Invoice Number": item.invoiceId,
      "Raw Material": item.rawMaterialName,
      Category: item.categoryName,
      Vendor: item.vendorName,
      "Invoiced Quantity": item.quantityWithUnit ?? item.invoicedQuantity,
      "Accepted Quantity": item.acceptedQuantityWithUnit ?? item.acceptedQuantity,
      "Remaining Quantity": item.remainingQuantityWithUnit ?? item.remainingQuantity,
      "Rejected Quantity": item.rejectedQuantityWithUnit ?? item.rejectedQuantity,
      "Basic Amount": rupee.format(parseNumber(item.basicAmount)),
      "Tax Amount": rupee.format(parseNumber(item.taxAmount)),
      "Total Amount": rupee.format(parseNumber(item.totalAmount)),
      "QC By": item.updatedByName,
      "QC Date": item.updatedDate
        ? new Date(item.updatedDate).toLocaleDateString("en-GB")
        : "",
      Action: qcStatus,
    };
  });
};

// ✅ Export function
export const rawMaterialExport = async (
  filter: RawMaterialPurchaseFilterExportDTO
) => {
  try {
    const data = await getRawMaterialPurchasesExport(filter);
    const rawMaterialList = data.rawMaterialPurchases || [];
    const qcStatuses = data.qcStatuses || [];

    if (!rawMaterialList.length) {
      console.warn("No data available for export.");
      return;
    }

    const orderData = rawMaterialWiseData(rawMaterialList, qcStatuses);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(orderData);
    XLSX.utils.book_append_sheet(wb, ws, "RawMaterials");
    XLSX.writeFile(wb, "RawMaterialListExport.xlsx");

    console.log(`✅ Exported ${orderData.length} records successfully.`);
  } catch (error) {
    console.error("❌ Error exporting raw material list:", error);
  }
};
